// =============================================================================
// its-sign-in.js - the main website's sign-in page (/authenticate) only
// =============================================================================
//
// NOT part of the subdomain kit and never copied to other apps. /authenticate is the
// one page ITS sends people back to after they log in, so only it talks to ITS:
//
//   1. startItsLogin()             user clicks Login -> ITS OneLogin
//   2. readItsTokenAndDT(search?)  read Token + DT from the URL ITS sends back
//   3. verifyToken(token, dt)      auth worker verifies Token, decrypts DT and sets
//                                  abnj_sso -> { exp, user, redirectUrl }, where
//                                  redirectUrl = the starting app's /callback
// =============================================================================

import { ABNJ_SSO_SUBDOMAIN_DEFAULTS } from './abnj-sso-subdomain';

/** ITS OneLogin for NJ Jamaat. */
const ITS_LOGIN_URL = 'https://www.its52.com/Login.aspx?OneLogin=NEWJERSEY';

/** Goes to the ITS OneLogin page. */
export function startItsLogin() {
  window.location.assign(ITS_LOGIN_URL);
}

/**
 * Reads Token and DT from the URL ITS redirected back to.
 * @param {string} [input]  a query string, path or full URL. Default: the current URL.
 * @returns {{ token: string, dt: string } | null}  null if either is missing
 */
export function readItsTokenAndDT(input = window.location.search) {
  const params = new URLSearchParams(input.includes('?') ? input.slice(input.indexOf('?')) : input);
  const token = params.get('Token');
  const dt = params.get('DT');
  return token && dt ? { token, dt } : null;
}

/**
 * Hands Token + DT to the auth worker, which verifies Token, decrypts DT and sets the
 * abnj_sso cookie. Also sends the origin of the app the user started from (from the
 * redirect_after_login cookie that app's signIn() saved; this site if they clicked
 * Login here), so the worker can answer with that app's /callback (redirectUrl).
 * Throws "App not registered" if that app isn't in allowed-origins.js.
 * @returns {Promise<{ exp: number, user: object, redirectUrl: string } | null>}  null = not a valid ITS sign-in
 */
export async function verifyToken(token, dt) {
  if (!token || !dt) return null;
  const saved = document.cookie.match(/(?:^|;\s*)redirect_after_login=([^;]*)/);
  const origin = new URL(saved ? decodeURIComponent(saved[1]) : window.location.href).origin;

  const res = await fetch(`${ABNJ_SSO_SUBDOMAIN_DEFAULTS.authUrl}/verify-its-token`, {
    method: 'POST',
    credentials: 'include', // receive the HttpOnly abnj_sso cookie
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ its_token: token, its_data: dt, origin }),
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`Auth worker /verify-its-token failed (${res.status}): ${await res.text()}`);
  const data = await res.json();
  return data?.success ? { exp: data.exp, user: data.user, redirectUrl: data.redirect_url } : null;
}
